import type { Snapshot, WireError } from 'shared';

/**
 * The connection as a pure reducer, so ordering and staleness rules can be
 * tested without a socket.
 *
 * Clients render purely from the snapshot — there is no client-side game logic
 * and no locally derived state that could disagree with another device.
 */
export type ConnectionStatus = 'connecting' | 'open' | 'closed';

export interface ConnectionState {
  status: ConnectionStatus;
  snapshot: Snapshot | null;
  /**
   * The immediately preceding snapshot. Animations are driven by comparing the
   * two, never by state of their own — so a device that joins or reloads into
   * an already-revealed vote renders the end state with no replay.
   */
  previous: Snapshot | null;
  error: WireError | null;
  /** Set once a join succeeds, so the join form can be retired immediately. */
  playerId: string | null;
}

export type ConnectionEvent =
  | { type: 'connecting' }
  | { type: 'connected' }
  | { type: 'disconnected' }
  | { type: 'snapshot'; snapshot: Snapshot }
  | { type: 'identity'; playerId: string }
  | { type: 'error'; error: WireError }
  | { type: 'dismissError' };

export const initialConnectionState: ConnectionState = {
  status: 'connecting',
  snapshot: null,
  previous: null,
  error: null,
  playerId: null,
};

export function connectionReducer(
  state: ConnectionState,
  event: ConnectionEvent,
): ConnectionState {
  switch (event.type) {
    case 'connecting':
      return { ...state, status: 'connecting' };

    case 'connected':
      return { ...state, status: 'open' };

    case 'disconnected':
      // The last snapshot is deliberately kept: a brief drop should not blank
      // the screen, and the next snapshot on reconnect replaces it wholesale.
      return { ...state, status: 'closed' };

    case 'snapshot': {
      const incoming = event.snapshot;
      const current = state.snapshot;

      // Versions are monotonic per room, so a lower one is a reordered
      // delivery and must not overwrite newer state.
      if (current && incoming.roomId === current.roomId && incoming.version < current.version) {
        return state;
      }

      return {
        ...state,
        status: 'open',
        previous: current,
        snapshot: incoming,
        playerId: incoming.viewer.playerId ?? state.playerId,
        // A successful mutation clears whatever the last complaint was.
        error: null,
      };
    }

    case 'identity':
      return { ...state, playerId: event.playerId };

    case 'error':
      return { ...state, error: event.error };

    case 'dismissError':
      return { ...state, error: null };
  }
}

/** True when the phase changed between the two snapshots the client holds. */
export function phaseChanged(state: ConnectionState): boolean {
  if (!state.snapshot || !state.previous) return false;
  return state.snapshot.phase !== state.previous.phase;
}

/** True on the transition into a reveal — the one moment worth a set-piece. */
export function justRevealed(state: ConnectionState): boolean {
  return (
    state.snapshot?.phase === 'REVEALED' &&
    state.previous !== null &&
    state.previous.phase !== 'REVEALED'
  );
}

/** True on the transition into an open vote, which is what alerts the table. */
export function voteJustOpened(state: ConnectionState): boolean {
  return (
    state.snapshot?.phase === 'VOTING' &&
    state.previous !== null &&
    state.previous.phase !== 'VOTING'
  );
}
